import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { initGA, trackPageView } from "./utils/visitorTracker";
import { initGoogleAds } from "./utils/googleAdsTracker";
import { ensureAuthReady } from "./services/otpService";
import ToastContainer from "./components/Toast";
import LandingPage from "./components/LandingPage";
import AddressSelection from "./components/AddressSelection";
import FromAddress from "./components/FromAddress";
import ToAddress from "./components/ToAddress";
import FlightDetails from "./components/FlightDetails";
import ItemDetails from "./components/ItemDetails";
import LogoAnimation from "./components/LogoAnimation";
import TravelerWaitlist from "./components/TravelerWaitlist";
import SenderWaitlist from "./components/SenderWaitlist";
import AppLayout from "./components/AppLayout";
import Dashboard from "./components/Dashboard";
import InstallPrompt from "./components/InstallPrompt";
import Loader from "./components/Loader";

/* ── Code-split routes ──────────────────────────────────────────────────────
   Everything used to be imported eagerly, so a single 2.25 MB bundle shipped
   on every visit — a Google Ads click on /travel/:slug downloaded the whole
   245 KB admin dashboard before it could paint, which defeats the point of
   CampaignLanding being built as pure CSS/SVG for speed.

   Split here = routes that are NOT on the user's hot path. The landing page,
   the address/item/flight forms, both waitlists and the dashboard stay EAGER
   on purpose: those are reached mid-journey (several right after OTP), and a
   chunk fetch there would put a spinner exactly where the user is already
   waiting. Admin, demo, the retired Rakhi pages and the dead routes can all
   afford one.
   ────────────────────────────────────────────────────────────────────────── */
const AdminLogin      = lazy(() => import("./components/Admin/AdminLogin"));
const AdminDashboard  = lazy(() => import("./components/Admin/AdminDashboard"));
const ConfirmPage     = lazy(() => import("./components/ConfirmPage"));
const CampaignLanding = lazy(() => import("./components/CampaignLanding"));
const DemoPage        = lazy(() => import("./components/DemoPage"));
const InfoPage        = lazy(() => import("./components/InfoPage"));
const TravelerProfile = lazy(() => import("./components/TravelerProfile"));
const SenderProfile   = lazy(() => import("./components/SenderProfile"));
const RakhiExpress    = lazy(() => import("./components/RakhiExpress"));
const RakhiBook       = lazy(() => import("./components/RakhiBook"));
const RakhiStatus     = lazy(() => import("./components/RakhiStatus"));
// Dead routes (CLAUDE.md) — kept routed, but they must not cost every visitor.
const PanVerification = lazy(() => import("./components/PanVerification"));
const TravelerList    = lazy(() => import("./components/TravelerList"));

function PageTracker() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  useEffect(() => {
    if (location.pathname.startsWith("/admin") && window.clarity) {
      window.clarity("stop");
    }
  }, [location.pathname]);
  return null;
}

/* Every route change is a client-side navigation, so the browser keeps the
   PREVIOUS page's scroll offset: scroll halfway down Help, open About, and
   About opens halfway down. On the dashboard that hid the name/number card
   under the fixed header ("cut off sometimes" — it depended on how far the
   last page had been scrolled). New navigations start at the top; back and
   forward (POP) are left alone so returning to a page keeps your place.
   Keyed on pathname only, so in-page #hash links and query changes don't jump. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, navigationType]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <PageTracker />
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<LogoAnimation onFinish={() => (window.location.href = "/login")} />} />
          <Route path="/intro" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LandingPage />} />
          <Route path="/selection" element={<AddressSelection />} />
          <Route path="/address-selection" element={<AddressSelection />} />
          <Route path="/from-address" element={<FromAddress />} />
          <Route path="/to-address" element={<ToAddress />} />
          <Route path="/flight-details" element={<FlightDetails />} />
          <Route path="/pan-verification" element={<PanVerification />} />
          <Route path="/item-details" element={<ItemDetails />} />
          <Route path="/traveler-list" element={<TravelerList />} />
          <Route path="/traveler-profile" element={<TravelerProfile />} />
          <Route path="/sender-profile" element={<SenderProfile />} />
          <Route path="/traveler-waitlist" element={<TravelerWaitlist />} />
          <Route path="/sender-waitlist" element={<SenderWaitlist />} />
          <Route path="/info/:type" element={<InfoPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/rakhi-express" element={<RakhiExpress />} />
          <Route path="/rakhi-express/book" element={<RakhiBook />} />
          <Route path="/rakhi-express/status" element={<RakhiStatus />} />
        </Route>
        {/* Route-specific traveller campaign landing pages (Google Ads) —
            deliberately outside AppLayout: no header/footer/bottom-nav, one
            CTA only. Routes are admin-managed in Firestore
            campaignRoutes/{slug} (AdminDashboard.jsx -> Settings). */}
        <Route path="/travel/:slug" element={<CampaignLanding />} />
        <Route path="/confirm/:token" element={<ConfirmPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>

    </>
  );
}

// Start Auth's session restore as early as possible. It is already happening
// in the background; claiming the promise here just means the FIRST Track tap
// no longer has to wait for it. Costs nothing — no network, no Firestore read,
// and ensureAuthReady() is memoised so later callers get the settled promise.
ensureAuthReady();

initGA(); // runs once at module load, no-ops if GA_ID not set yet
initGoogleAds(); // runs once at module load, no-ops if GOOGLE_ADS_ID not set (staging)

export default function App() {
  return (
    <Router>
      <ToastContainer />
      <AppRoutes />
      <InstallPrompt />
    </Router>
  );
}
